import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Navigate } from "react-router-dom";
import {
  goldenEtiquetar,
  goldenExportUrl,
  goldenGrupos,
  goldenItems,
  goldenMuestra,
  goldenOpciones,
  goldenResumen,
  type GoldenAcuerdo,
  type GoldenGrupo,
  type GoldenItem,
  type GoldenOpciones,
  type GoldenTipo,
} from "../api";
import { useDocumentTheme } from "../components/social/useDocumentTheme";
import { useDashboard } from "../context/DashboardContext";
import "../components/social/mensajes/mensajes.css";
import "./golden.css";

const PLATAFORMAS: Record<GoldenTipo, Array<[string, string]>> = {
  comentario: [["meta_ig", "Instagram"], ["meta_fb", "Facebook"], ["gbp", "Google"], ["youtube", "YouTube"]],
  dm: [["messenger", "Messenger"], ["instagram", "Instagram"]],
};
const NOMBRE_PLATAFORMA: Record<string, string> = Object.fromEntries(Object.values(PLATAFORMAS).flat());
const PREGUNTAS: Record<string, string> = {
  polaridad: "Polaridad",
  acto: "Acto",
  aspecto: "Aspecto",
  accionable: "¿Alguien del equipo debe actuar?",
  escalar: "¿Debe verlo una persona senior antes de responder?",
  requiere_respuesta: "¿Pide una respuesta?",
  es_lead: "¿Es un posible lead (paciente o cliente que quiere contactar o comprar)?",
};
const METRICAS = [
  ["acuerdo_polaridad", "Polaridad"],
  ["acuerdo_acto", "Acto"],
  ["acuerdo_aspecto", "Aspecto"],
  ["acuerdo_lead", "Lead"],
  ["acuerdo_responder", "Responder"],
] as const;
const UMBRAL = 0.95;
const MIN_CASOS = 30;

const pct = (v: number | null) => (v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`);
const etiqueta = (v: string) => v.replace(/_/g, " ");

const Z_95 = 1.645; // 95 % de confianza, un solo lado

// Cotas de Wilson del acierto verdadero dado el acierto observado p en n casos.
function cotas(p: number, n: number): [number, number] {
  const z2 = Z_95 * Z_95;
  const d = 1 + z2 / n;
  const centro = p + z2 / (2 * n);
  const margen = Z_95 * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n));
  return [(centro - margen) / d, (centro + margen) / d];
}

type Veredicto = "pasa" | "no_pasa" | "sin_concluir";
function veredicto(p: number | null, n: number): Veredicto {
  if (p === null || n < MIN_CASOS) return "sin_concluir";
  const [bajo, alto] = cotas(p, n);
  return bajo >= UMBRAL ? "pasa" : alto < UMBRAL ? "no_pasa" : "sin_concluir";
}

function estadoGrupo(a: GoldenAcuerdo): { clase: string; texto: string } {
  if (a.etiquetados < MIN_CASOS) return { clase: "now", texto: `Faltan ${MIN_CASOS - a.etiquetados} casos` };
  const vs = METRICAS.filter(([k]) => a[k] !== null).map(([k]) => veredicto(a[k], a.etiquetados));
  if (vs.includes("no_pasa")) return { clase: "lead", texto: "No pasa" };
  return vs.every((v) => v === "pasa") ? { clase: "ok", texto: "Pasa" } : { clase: "now", texto: "Sin concluir" };
}

export function GoldenPage() {
  const { session, selectedClient, data: dashboardData } = useDashboard();
  const theme = useDocumentTheme();
  const clientes = dashboardData?.clients ?? [];
  const [cliente, setCliente] = useState(selectedClient !== "ALL" ? selectedClient : "");
  const [tipo, setTipo] = useState<GoldenTipo>("comentario");
  const [plataforma, setPlataforma] = useState("meta_ig");
  const [items, setItems] = useState<GoldenItem[]>([]);
  const [opciones, setOpciones] = useState<GoldenOpciones | null>(null);
  const [indice, setIndice] = useState(0);
  const [grupos, setGrupos] = useState<GoldenGrupo[]>([]);
  const [acuerdos, setAcuerdos] = useState<GoldenAcuerdo[]>([]);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!PLATAFORMAS[tipo].some(([id]) => id === plataforma)) setPlataforma(PLATAFORMAS[tipo][0][0]);
  }, [tipo, plataforma]);

  const recargarResumen = useCallback(() => {
    void goldenGrupos().then(setGrupos).catch(() => undefined);
    void goldenResumen().then(setAcuerdos).catch(() => undefined);
  }, []);
  useEffect(recargarResumen, [recargarResumen]);

  useEffect(() => {
    if (!cliente) {
      setItems([]);
      return;
    }
    let activo = true;
    setCargando(true);
    setMensaje(null);
    Promise.all([goldenOpciones(cliente), goldenItems(cliente, plataforma, tipo)])
      .then(([o, lista]) => {
        if (!activo) return;
        setOpciones(o);
        setItems(lista);
        const primero = lista.findIndex((i) => !i.etiquetado);
        setIndice(primero < 0 ? 0 : primero);
      })
      .catch((e: unknown) => activo && setMensaje(e instanceof Error ? e.message : "No se pudo cargar"))
      .finally(() => activo && setCargando(false));
    return () => {
      activo = false;
    };
  }, [cliente, plataforma, tipo]);

  const campos = opciones?.campos[tipo] ?? [];
  const actual = items[indice];
  const completo = useCallback((i: GoldenItem) => campos.length > 0 && campos.every((c) => Boolean(i[c])), [campos]);
  const hechos = useMemo(() => items.filter(completo).length, [items, completo]);
  const nombreCliente = (slug: string) => clientes.find((c) => c.clientSlug === slug)?.displayName ?? slug;

  if (session && !session.canEditGolden) return <Navigate to="/" replace />;

  const elegir = (campo: string, valor: string) => {
    if (!actual) return;
    setItems((prev) => prev.map((i) => (i.golden_id === actual.golden_id ? { ...i, [campo]: valor, etiquetado: true } : i)));
    goldenEtiquetar(actual.golden_id, { [campo]: valor }).then(recargarResumen).catch((e: unknown) => setMensaje(e instanceof Error ? e.message : "No se pudo guardar"));
  };
  const guardarNota = (nota: string) => {
    if (!actual) return;
    goldenEtiquetar(actual.golden_id, { nota }).catch(() => undefined);
  };
  const generar = () => {
    setMensaje(null);
    goldenMuestra(cliente, plataforma, tipo)
      .then((r) => {
        setMensaje(r.agregados > 0 ? `Se agregaron ${r.agregados} casos.` : "No hay casos nuevos para esta combinación.");
        return goldenItems(cliente, plataforma, tipo).then((lista) => {
          setItems(lista);
          recargarResumen();
        });
      })
      .catch((e: unknown) => setMensaje(e instanceof Error ? e.message : "No se pudo generar la muestra"));
  };
  const siguienteSinEtiquetar = () => {
    const i = items.findIndex((x, n) => n > indice && !completo(x));
    setIndice(i < 0 ? Math.max(items.findIndex((x) => !completo(x)), 0) : i);
  };
  const abrirGrupo = (a: GoldenAcuerdo) => {
    setCliente(a.client_slug);
    setTipo(a.tipo);
    setPlataforma(a.plataforma);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="page msj gld" data-theme={theme}>
      <header className="msj-head">
        <div className="msj-head-t">
          <h1>Golden sets</h1>
          <p className="msj-sub">Etiqueta a ciegas: no ves lo que dijo el clasificador. Cada cliente, plataforma y tipo se mantiene por separado.</p>
        </div>
        <div className="msj-seg" role="group" aria-label="Tipo">
          {(["comentario", "dm"] as GoldenTipo[]).map((t) => (
            <button key={t} type="button" aria-pressed={tipo === t} onClick={() => setTipo(t)}>{t === "comentario" ? "Comentarios" : "Mensajes directos"}</button>
          ))}
        </div>
      </header>

      <section className="msj-card gld-filtros" aria-label="Filtros">
        <label className="gld-campo">Cliente
          <select value={cliente} onChange={(e) => setCliente(e.target.value)}>
            <option value="">Elige un cliente</option>
            {clientes.map((c) => <option key={c.clientSlug} value={c.clientSlug}>{c.displayName}</option>)}
          </select>
        </label>
        <div className="msj-chips gld-plat" role="group" aria-label="Plataforma">
          {PLATAFORMAS[tipo].map(([id, nombre]) => (
            <button key={id} type="button" className="msj-chip" aria-pressed={plataforma === id} onClick={() => setPlataforma(id)}>{nombre}</button>
          ))}
        </div>
        <button type="button" className="msj-btn primary" disabled={!cliente} onClick={generar}>Generar muestra de 40</button>
      </section>

      {mensaje && <p className="gld-aviso" role="status">{mensaje}</p>}

      {!cliente ? (
        <div className="msj-card msj-vacio">Elige un cliente para etiquetar.</div>
      ) : cargando ? (
        <div className="msj-card msj-vacio gld-cargando" role="status"><span className="gld-spinner" aria-hidden="true" />Cargando…</div>
      ) : items.length === 0 ? (
        <div className="msj-card msj-vacio">Aún no hay casos para esta combinación. Genera una muestra.</div>
      ) : (
        <>
          <section className="msj-kpis gld-kpis" aria-label="Avance">
            <div className="msj-kpi"><div className="l">Casos</div><div className="v">{items.length}</div></div>
            <div className="msj-kpi"><div className="l">Completos</div><div className="v">{hechos}</div></div>
            <div className="msj-kpi"><div className="l">Pendientes</div><div className="v">{items.length - hechos}</div></div>
          </section>

          <section className="msj-card gld-caso" aria-label="Caso a etiquetar">
            <div className="gld-prog">
              <span>Caso {indice + 1} de {items.length}</span>
              <div className="gld-bar" role="progressbar" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={hechos}><i style={{ width: `${(100 * hechos) / items.length}%` }} /></div>
            </div>
            <div className="gld-tags">
              <span className="msj-tag">{NOMBRE_PLATAFORMA[actual.plataforma] ?? actual.plataforma}</span>
              {actual.estrellas ? <span className="msj-tag">{actual.estrellas} ★</span> : null}
              {completo(actual) && <span className="msj-tag ok">Completo</span>}
            </div>
            {(actual.post_titulo || actual.post_url) && (
              <p className="gld-post">
                <span>{actual.post_titulo || "Publicación sin título"}</span>
                {actual.post_url?.startsWith("https://") && (
                  <a href={actual.post_url} target="_blank" rel="noopener noreferrer">
                    {actual.plataforma === "gbp" ? "Ver ficha" : "Ver publicación"} <ExternalLink size={14} aria-hidden="true" />
                  </a>
                )}
              </p>
            )}
            <p className="gld-texto">{actual.texto}</p>
            {campos.map((campo) => (
              <div key={campo} className="gld-pregunta">
                <div className="q">{PREGUNTAS[campo] ?? campo}</div>
                <div className="msj-chips" role="group" aria-label={PREGUNTAS[campo] ?? campo}>
                  {(opciones?.opciones[campo] ?? []).map((valor) => (
                    <button key={valor} type="button" className="msj-chip" aria-pressed={actual[campo] === valor} onClick={() => elegir(campo, valor)}>{etiqueta(valor)}</button>
                  ))}
                </div>
              </div>
            ))}
            <label className="gld-campo">Nota (opcional)
              <textarea key={actual.golden_id} defaultValue={actual.nota ?? ""} onBlur={(e) => guardarNota(e.target.value)} />
            </label>
            <div className="gld-nav">
              <button type="button" className="msj-btn" disabled={indice === 0} onClick={() => setIndice(indice - 1)}>Anterior</button>
              <button type="button" className="msj-btn" disabled={indice >= items.length - 1} onClick={() => setIndice(indice + 1)}>Siguiente</button>
              <button type="button" className="msj-btn primary" onClick={siguienteSinEtiquetar}>Siguiente sin etiquetar</button>
            </div>
          </section>
        </>
      )}

      <section aria-label="Acuerdo con el clasificador">
        <div className="msj-sec-h">
          <h2>Acuerdo con el clasificador</h2>
          <span>Pasa cuando, con 95% de confianza, cada medida acierta al menos {Math.round(UMBRAL * 100)}% (mínimo {MIN_CASOS} casos; sin errores se logra con 52).</span>
        </div>
        <div className="gld-grupos">
          {acuerdos.map((a) => {
            const estado = estadoGrupo(a);
            const total = grupos.find((g) => g.client_slug === a.client_slug && g.plataforma === a.plataforma && g.tipo === a.tipo)?.total;
            return (
              <article key={`${a.client_slug}${a.plataforma}${a.tipo}`} className="msj-card gld-grupo">
                <div className="gld-grupo-h">
                  <div>
                    <h3>{nombreCliente(a.client_slug)}</h3>
                    <p className="msj-note">{NOMBRE_PLATAFORMA[a.plataforma] ?? a.plataforma} · {a.tipo === "dm" ? "mensajes directos" : "comentarios"} · {a.etiquetados}/{total ?? "—"} etiquetados</p>
                  </div>
                  <span className={`msj-tag ${estado.clase}`}>{estado.texto}</span>
                </div>
                <div className="gld-metricas">
                  {METRICAS.filter(([k]) => a[k] !== null).map(([k, nombre]) => (
                    <div key={k} className={`gld-metrica${{ pasa: " ok", no_pasa: " mal", sin_concluir: "" }[veredicto(a[k], a.etiquetados)]}`}>
                      <span>{nombre}</span><b>{pct(a[k])}</b>
                    </div>
                  ))}
                </div>
                <button type="button" className="msj-btn" onClick={() => abrirGrupo(a)}>Etiquetar este grupo</button>
              </article>
            );
          })}
          {acuerdos.length === 0 && <div className="msj-card msj-vacio">Sin casos etiquetados todavía.</div>}
        </div>
        <p className="gld-export">
          <a href={goldenExportUrl("comentario", cliente || undefined)}>Exportar comentarios (CSV)</a>
          <a href={goldenExportUrl("dm", cliente || undefined)}>Exportar mensajes directos (CSV)</a>
        </p>
      </section>
    </div>
  );
}
